<!--
  Copyright (c) 2025 NoorWays. All rights reserved.
  Licensed under the terms specified in the LICENSE.txt file.
-->

<template>
  <div class="grammar-checker-page">
    <!-- SEO-Optimized Hero Section -->
    <header class="hero">
      <div class="container">
        <h1 class="hero-title">{{ $t('seo.grammarChecker.title') }}</h1>
        <p class="hero-subtitle">{{ $t('seo.grammarChecker.description') }}</p>
        <div class="hero-actions">
          <router-link to="/editor" class="btn btn-primary btn-large">
            {{ $t('grammarChecker.tryFree') }}
          </router-link>
          <router-link to="/pricing" class="btn btn-secondary">
            {{ $t('grammarChecker.viewPricing') }}
          </router-link>
        </div>
        <div class="trust-indicators">
          <span class="trust-item">✓ {{ $t('grammarChecker.trust.free') }}</span>
          <span class="trust-item">✓ {{ $t('grammarChecker.trust.noSignup') }}</span>
          <span class="trust-item">✓ {{ $t('grammarChecker.trust.multilingual') }}</span>
        </div>
      </div>
    </header>

    <!-- Features Comparison Section -->
    <section class="features-comparison">
      <div class="container">
        <h2>{{ $t('grammarChecker.comparison.title') }}</h2>
        <div class="comparison-table">
          <div class="comparison-header">
            <div class="feature-col">{{ $t('grammarChecker.comparison.feature') }}</div>
            <div class="noorwriter-col">n.writer</div>
            <div class="competitor-col">{{ $t('grammarChecker.comparison.others') }}</div>
          </div>
          <div class="comparison-row">
            <div class="feature-col">{{ $t('grammarChecker.features.aiPowered') }}</div>
            <div class="noorwriter-col">✅</div>
            <div class="competitor-col">❌</div>
          </div>
          <div class="comparison-row">
            <div class="feature-col">{{ $t('grammarChecker.features.multilingual') }}</div>
            <div class="noorwriter-col">✅ {{ $t('grammarChecker.trust.multilingual') }}</div>
            <div class="competitor-col">❌ {{ $t('grammarChecker.limited') }}</div>
          </div>
          <div class="comparison-row">
            <div class="feature-col">{{ $t('grammarChecker.features.freeUsage') }}</div>
            <div class="noorwriter-col">✅ 10 {{ $t('grammarChecker.dailyFree') }}</div>
            <div class="competitor-col">❌ {{ $t('grammarChecker.limitedFree') }}</div>
          </div>
          <div class="comparison-row">
            <div class="feature-col">{{ $t('grammarChecker.features.contextual') }}</div>
            <div class="noorwriter-col">✅</div>
            <div class="competitor-col">❌</div>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="how-it-works">
      <div class="container">
        <h2>{{ $t('grammarChecker.howItWorks.title') }}</h2>
        <div class="steps-grid">
          <div class="step">
            <div class="step-number">1</div>
            <h3>{{ $t('grammarChecker.howItWorks.step1.title') }}</h3>
            <p>{{ $t('grammarChecker.howItWorks.step1.description') }}</p>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <h3>{{ $t('grammarChecker.howItWorks.step2.title') }}</h3>
            <p>{{ $t('grammarChecker.howItWorks.step2.description') }}</p>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <h3>{{ $t('grammarChecker.howItWorks.step3.title') }}</h3>
            <p>{{ $t('grammarChecker.howItWorks.step3.description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits Section -->
    <section class="benefits">
      <div class="container">
        <h2>{{ $t('grammarChecker.benefits.title') }}</h2>
        <div class="benefits-grid">
          <div class="benefit-card">
            <h3>{{ $t('grammarChecker.benefits.accuracy.title') }}</h3>
            <p>{{ $t('grammarChecker.benefits.accuracy.description') }}</p>
          </div>
          <div class="benefit-card">
            <h3>{{ $t('grammarChecker.benefits.speed.title') }}</h3>
            <p>{{ $t('grammarChecker.benefits.speed.description') }}</p>
          </div>
          <div class="benefit-card">
            <h3>{{ $t('grammarChecker.benefits.learning.title') }}</h3>
            <p>{{ $t('grammarChecker.benefits.learning.description') }}</p>
          </div>
          <div class="benefit-card">
            <h3>{{ $t('grammarChecker.benefits.privacy.title') }}</h3>
            <p>{{ $t('grammarChecker.benefits.privacy.description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section for Voice Search -->
    <section class="faq">
      <div class="container">
        <h2>{{ $t('grammarChecker.faq.title') }}</h2>
        <div class="faq-list">
          <div class="faq-item">
            <h3>{{ $t('grammarChecker.faq.q1.question') }}</h3>
            <p>{{ $t('grammarChecker.faq.q1.answer') }}</p>
          </div>
          <div class="faq-item">
            <h3>{{ $t('grammarChecker.faq.q2.question') }}</h3>
            <p>{{ $t('grammarChecker.faq.q2.answer') }}</p>
          </div>
          <div class="faq-item">
            <h3>{{ $t('grammarChecker.faq.q3.question') }}</h3>
            <p>{{ $t('grammarChecker.faq.q3.answer') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta">
      <div class="container">
        <h2>{{ $t('grammarChecker.cta.title') }}</h2>
        <p>{{ $t('grammarChecker.cta.description') }}</p>
        <router-link to="/editor" class="btn btn-primary btn-large">
          {{ $t('grammarChecker.cta.button') }}
        </router-link>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// SEO optimization for Grammar Checker page
useHead({
  title: () => t('seo.grammarChecker.title'),
  meta: [
    {
      name: 'description',
      content: () => t('seo.grammarChecker.description'),
    },
    {
      name: 'keywords',
      content: () => t('seo.grammarChecker.keywords'),
    },
    {
      property: 'og:title',
      content: () => t('seo.grammarChecker.title'),
    },
    {
      property: 'og:description',
      content: () => t('seo.grammarChecker.description'),
    },
    {
      property: 'og:url',
      content: 'https://n-writer.app/grammar-checker',
    },
    {
      name: 'twitter:title',
      content: () => t('seo.grammarChecker.title'),
    },
    {
      name: 'twitter:description',
      content: () => t('seo.grammarChecker.description'),
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: 'https://n-writer.app/grammar-checker',
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: t('seo.grammarChecker.title'),
        description: t('seo.grammarChecker.description'),
        url: 'https://n-writer.app/grammar-checker',
        mainEntity: {
          '@type': 'SoftwareApplication',
          name: 'n.writer Grammar Checker',
          applicationCategory: 'WritingApplication',
          operatingSystem: 'Web Browser',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
          },
        },
      }),
    },
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: t('grammarChecker.faq.q1.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('grammarChecker.faq.q1.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('grammarChecker.faq.q2.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('grammarChecker.faq.q2.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('grammarChecker.faq.q3.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('grammarChecker.faq.q3.answer'),
            },
          },
        ],
      }),
    },
    // HowTo Schema for Grammar Checker
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: t('grammarChecker.howItWorks.title'),
        description: t('geo.definitions.grammarChecker'),
        step: [
          {
            '@type': 'HowToStep',
            position: 1,
            name: t('grammarChecker.howItWorks.step1.title'),
            text: t('grammarChecker.howItWorks.step1.description'),
          },
          {
            '@type': 'HowToStep',
            position: 2,
            name: t('grammarChecker.howItWorks.step2.title'),
            text: t('grammarChecker.howItWorks.step2.description'),
          },
          {
            '@type': 'HowToStep',
            position: 3,
            name: t('grammarChecker.howItWorks.step3.title'),
            text: t('grammarChecker.howItWorks.step3.description'),
          },
        ],
      }),
    },
  ],
})
</script>

<style lang="scss" scoped>
.grammar-checker-page {
  min-height: 100vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.hero {
  background: linear-gradient(135deg, var(--nw-brand-slate) 0%, #0f172a 100%);
  color: white;
  padding: 5rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;

  // Animated background orbs
  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.3;
    animation: float 20s ease-in-out infinite;
  }

  &::before {
    width: 400px;
    height: 400px;
    background: var(--nw-primary);
    top: -100px;
    right: -100px;
  }

  &::after {
    width: 300px;
    height: 300px;
    background: var(--nw-primary-light);
    bottom: -50px;
    left: -50px;
    animation-delay: -10s;
  }

  .hero-title {
    font-size: 3.5rem;
    font-weight: 800;
    margin-bottom: 1.5rem;
    line-height: 1.1;
    position: relative;
    z-index: 1;
  }

  .hero-subtitle {
    font-size: 1.25rem;
    margin-bottom: 2rem;
    opacity: 0.9;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    z-index: 1;
  }

  .hero-actions {
    margin-bottom: 2rem;
    position: relative;
    z-index: 1;

    .btn {
      margin: 0 0.5rem;
    }
  }

  .trust-indicators {
    display: flex;
    justify-content: center;
    gap: 2rem;
    flex-wrap: wrap;
    position: relative;
    z-index: 1;

    .trust-item {
      font-size: 0.95rem;
      opacity: 0.9;
      font-weight: 500;
    }
  }
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(30px, -30px) scale(1.05); }
  50% { transform: translate(-20px, 20px) scale(0.95); }
  75% { transform: translate(20px, 10px) scale(1.02); }
}

.btn {
  padding: 1rem 2rem;
  border-radius: 0.75rem;
  text-decoration: none;
  font-weight: 700;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-block;
  border: none;
  cursor: pointer;

  &-primary {
    background: linear-gradient(135deg, var(--nw-primary) 0%, var(--nw-primary-dark) 100%);
    color: white;
    box-shadow: 0 4px 15px var(--nw-primary-40);

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 25px var(--nw-primary-40);
    }
  }

  &-secondary {
    background: rgba(255, 255, 255, 0.95);
    color: var(--nw-brand-slate);

    &:hover {
      background: white;
      transform: translateY(-2px);
    }
  }

  &-large {
    padding: 1.25rem 2.5rem;
    font-size: 1.1rem;
  }
}

.features-comparison,
.how-it-works,
.benefits,
.faq,
.cta {
  padding: 5rem 0;

  h2 {
    text-align: center;
    font-size: 2.5rem;
    font-weight: 800;
    color: var(--nw-brand-slate);
    margin-bottom: 3rem;
  }
}

.comparison-table {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 0;
  margin-top: 2rem;
  background: white;
  border-radius: 1.5rem;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--nw-border-light);

  .comparison-header {
    display: contents;
    font-weight: 700;
    
    > div {
      background: var(--nw-bg-secondary);
      padding: 1.25rem 1.5rem;
    }
  }

  .comparison-row {
    display: contents;

    > div {
      padding: 1.25rem 1.5rem;
      border-bottom: 1px solid var(--nw-border-light);
    }

    &:last-child > div {
      border-bottom: none;
    }
  }
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.step {
  text-align: center;
  padding: 2.5rem 2rem;
  background: white;
  border-radius: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--nw-border-light);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
  }

  .step-number {
    width: 70px;
    height: 70px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--nw-primary) 0%, var(--nw-primary-dark) 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.75rem;
    font-weight: 800;
    margin: 0 auto 1.5rem;
    box-shadow: 0 8px 20px var(--nw-primary-40);
  }

  h3 {
    color: var(--nw-brand-slate);
    font-weight: 700;
    margin-bottom: 1rem;
  }

  p {
    color: var(--nw-text-secondary);
    line-height: 1.6;
  }
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.benefit-card {
  padding: 2.5rem 2rem;
  background: white;
  border: 1px solid var(--nw-border-light);
  border-radius: 1.5rem;
  text-align: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px var(--nw-primary-20);
    border-color: var(--nw-primary-20);
  }

  h3 {
    color: var(--nw-brand-slate);
    font-weight: 700;
    margin-bottom: 1rem;
  }

  p {
    color: var(--nw-text-secondary);
    line-height: 1.6;
  }
}

.faq-list {
  max-width: 800px;
  margin: 2rem auto 0;
}

.faq-item {
  margin-bottom: 1.5rem;
  background: white;
  padding: 2rem;
  border-radius: 1rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--nw-border-light);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  }

  h3 {
    margin-bottom: 0.75rem;
    color: var(--nw-brand-slate);
    font-weight: 700;
  }

  p {
    color: var(--nw-text-secondary);
    line-height: 1.6;
  }
}

.cta {
  background: linear-gradient(135deg, var(--nw-brand-slate) 0%, #0f172a 100%);
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 300px;
    height: 300px;
    background: var(--nw-primary);
    border-radius: 50%;
    filter: blur(100px);
    opacity: 0.2;
    top: -100px;
    left: 50%;
    transform: translateX(-50%);
  }

  h2 {
    color: white;
    position: relative;
    z-index: 1;
  }

  p {
    color: rgba(255, 255, 255, 0.9);
    font-size: 1.1rem;
    margin-bottom: 2rem;
    position: relative;
    z-index: 1;
  }

  .btn {
    position: relative;
    z-index: 1;
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }

  .comparison-table {
    grid-template-columns: 1fr;

    .comparison-header {
      display: none;
    }
  }

  .trust-indicators {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
